import { useEffect, useState } from "react";
import { api, type Jurisdiction } from "./api";
import { isHiddenJurisdiction } from "./jurisdiction";

// Fetch the catalog once per session and share it across every mount (the new
// project dialog, onboarding, the per-project jurisdiction dialog). The catalog
// is small and effectively static, so a single in-flight promise is plenty.
let cache: Promise<Jurisdiction[]> | null = null;

function loadJurisdictions(): Promise<Jurisdiction[]> {
  if (!cache) {
    cache = api
      .listJurisdictions()
      // Drop jurisdictions the client hides (e.g. the default `ru`) at the
      // single fetch chokepoint, so every consumer gets a ready-to-show list.
      .then((r) => (r.jurisdictions ?? []).filter((j) => !isHiddenJurisdiction(j.code)))
      .catch(() => {
        // A failed or empty catalog must not break project creation: reset the
        // cache so a later mount can retry, and hand back an empty list. The UI
        // then hides the field and the server applies the default jurisdiction.
        cache = null;
        return [];
      });
  }
  return cache;
}

export type UseJurisdictions = {
  options: Jurisdiction[];
  loaded: boolean;
};

export function useJurisdictions(): UseJurisdictions {
  const [options, setOptions] = useState<Jurisdiction[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    void loadJurisdictions().then((list) => {
      if (!alive) return;
      setOptions(list);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { options, loaded };
}
